/**
 * 离线存储服务 - 使用IndexedDB
 */

const DB_NAME = 'wewerss_offline';
const DB_VERSION = 1;
const STORE_ARTICLES = 'articles';
const STORE_SYNC = 'sync_queue';

export interface OfflineArticle {
  id: string;
  title: string;
  content: string;
  summary?: string;
  subscription_id: string;
  subscription_name?: string;
  published_at: string;
  saved_at: string;
  is_read: boolean;
  link?: string;
  author?: string;
  description?: string;
  ai_tags?: string[];
  reading_time_minutes?: number;
}

export interface SyncAction {
  id?: number;
  type: 'mark_read' | 'mark_favorite';
  article_id: string;
  data: any;
  created_at: string;
}

class OfflineStorage {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // 文章存储
        if (!db.objectStoreNames.contains(STORE_ARTICLES)) {
          const store = db.createObjectStore(STORE_ARTICLES, { keyPath: 'id' });
          store.createIndex('saved_at', 'saved_at', { unique: false });
          store.createIndex('subscription_id', 'subscription_id', { unique: false });
          store.createIndex('is_read', 'is_read', { unique: false });
        }

        // 同步队列
        if (!db.objectStoreNames.contains(STORE_SYNC)) {
          const store = db.createObjectStore(STORE_SYNC, { keyPath: 'id', autoIncrement: true });
          store.createIndex('created_at', 'created_at', { unique: false });
        }
      };
    });
  }

  /**
   * 保存文章到离线存储
   */
  async saveArticle(article: OfflineArticle): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_ARTICLES, 'readwrite');
      const store = tx.objectStore(STORE_ARTICLES);
      const request = store.put({
        ...article,
        saved_at: article.saved_at || new Date().toISOString(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 批量保存文章
   */
  async saveArticles(articles: OfflineArticle[]): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_ARTICLES, 'readwrite');
      const store = tx.objectStore(STORE_ARTICLES);

      let completed = 0;
      const errors: any[] = [];

      articles.forEach((article) => {
        const request = store.put({
          ...article,
          saved_at: article.saved_at || new Date().toISOString(),
        });

        request.onsuccess = () => {
          completed++;
          if (completed === articles.length) {
            if (errors.length > 0) {
              reject(new Error(`Failed to save ${errors.length} articles`));
            } else {
              resolve();
            }
          }
        };

        request.onerror = () => {
          errors.push(request.error);
          completed++;
          if (completed === articles.length) {
            reject(new Error(`Failed to save ${errors.length} articles`));
          }
        };
      });
    });
  }

  /**
   * 获取单篇文章
   */
  async getArticle(id: string): Promise<OfflineArticle | undefined> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_ARTICLES, 'readonly');
      const store = tx.objectStore(STORE_ARTICLES);
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 获取所有离线文章
   */
  async getAllArticles(): Promise<OfflineArticle[]> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_ARTICLES, 'readonly');
      const store = tx.objectStore(STORE_ARTICLES);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 按订阅源获取文章
   */
  async getArticlesBySubscription(subscriptionId: string): Promise<OfflineArticle[]> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_ARTICLES, 'readonly');
      const store = tx.objectStore(STORE_ARTICLES);
      const index = store.index('subscription_id');
      const request = index.getAll(subscriptionId);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 删除文章
   */
  async deleteArticle(id: string): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_ARTICLES, 'readwrite');
      const store = tx.objectStore(STORE_ARTICLES);
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 批量删除文章
   */
  async deleteArticles(ids: string[]): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_ARTICLES, 'readwrite');
      const store = tx.objectStore(STORE_ARTICLES);

      let completed = 0;
      const errors: any[] = [];

      ids.forEach((id) => {
        const request = store.delete(id);
        request.onsuccess = () => {
          completed++;
          if (completed === ids.length) {
            if (errors.length > 0) {
              reject(new Error(`Failed to delete ${errors.length} articles`));
            } else {
              resolve();
            }
          }
        };
        request.onerror = () => {
          errors.push(request.error);
          completed++;
          if (completed === ids.length) {
            reject(new Error(`Failed to delete ${errors.length} articles`));
          }
        };
      });
    });
  }

  /**
   * 获取离线文章数量
   */
  async getOfflineCount(): Promise<number> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_ARTICLES, 'readonly');
      const store = tx.objectStore(STORE_ARTICLES);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 清空所有离线文章
   */
  async clearAll(): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_ARTICLES, 'readwrite');
      const store = tx.objectStore(STORE_ARTICLES);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 检查文章是否已保存离线
   */
  async isArticleSaved(id: string): Promise<boolean> {
    const article = await this.getArticle(id);
    return !!article;
  }

  /**
   * 添加同步动作到队列
   */
  async addSyncAction(action: SyncAction): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_SYNC, 'readwrite');
      const store = tx.objectStore(STORE_SYNC);
      const request = store.add({
        ...action,
        created_at: action.created_at || new Date().toISOString(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 获取所有待同步动作
   */
  async getSyncActions(): Promise<SyncAction[]> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_SYNC, 'readonly');
      const store = tx.objectStore(STORE_SYNC);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 清空同步队列
   */
  async clearSyncQueue(): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_SYNC, 'readwrite');
      const store = tx.objectStore(STORE_SYNC);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 删除特定同步动作
   */
  async deleteSyncAction(id: number): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_SYNC, 'readwrite');
      const store = tx.objectStore(STORE_SYNC);
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 获取存储使用情况
   */
  async getStorageInfo(): Promise<{
    articleCount: number;
    syncQueueCount: number;
    estimatedSize: number;
  }> {
    if (!this.db) await this.init();

    const articleCount = await this.getOfflineCount();
    const syncActions = await this.getSyncActions();
    const syncQueueCount = syncActions.length;

    // 粗略估算大小（每篇文章约50KB）
    const estimatedSize = articleCount * 50 * 1024;

    return {
      articleCount,
      syncQueueCount,
      estimatedSize,
    };
  }
}

export const offlineStorage = new OfflineStorage();
